﻿import React from 'react'
import { Button, Heading, Text, LoadingSpinner, Input } from '../../components/ui'
import { SortableTableHeader } from '../../components/ui/SortableTableHeader'
import { supabase } from '../../lib/supabase'
import { admDespesasService } from '../../services/admDespesas'
import { hasAdmNotaDownloadKey, runAdmStorageSignedDownload } from '../../utils/admDespesasStorageDownload'
import { matchesPaymentDatePeriod } from '../../utils/admPaymentDateFilter'
import { PaymentDatePeriodFilterFields } from '../../components/adm/PaymentDatePeriodFilterFields'
import { printService } from '../../services/printService'
import { logDelete, logUpdate } from '../../services/userLogs'
import type { AdmOrderHeader, AdmOrderItem } from '../../types/admDespesas'
import type { AdmCategoria, AdmCentroCusto } from '../../types/admCadastros'
import type { PrintConfig, PrintColumn, PrintRow } from '../../types/print'
import { ArrowDownTrayIcon, DocumentIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import AdmDespesasBudgetEditModal, {
  budgetEditPatchFromModal,
  budgetEditStateFromRecord,
  type BudgetEditModalState,
} from '../../components/adm/AdmDespesasBudgetEditModal'

const PAGE_SIZE = 20
/** Pasta no bucket ADM (mesma que NF-e/contratos: `notas/`). */
const ADM_BUDGET_STORAGE_FOLDER = 'notas'

const FILTRO_NOME_ARQUIVO = 'Nome do arquivo'
const FILTRO_NOME_ARQUIVO_TITLE = 'Filtra pelo texto da coluna file_name no cabeçalho.'

type BudgetsMainSubTab = 'headers' | 'items'

function fileNameQueryMatches(fileNameColumn: string | null | undefined, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (fileNameColumn ?? '').toLowerCase().includes(q)
}

function formatCurrency(value: number | null): string {
  if (value == null) return '—'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatQuantity(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 6 })
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('pt-BR')
}

function formatDocument(value: string | null): string {
  if (!value) return '—'
  const digits = value.replace(/\D/g, '')
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }
  return value
}

function formatDocumentKind(kind: string | null): string {
  const k = (kind ?? '').trim().toLowerCase()
  if (k === 'orcamento') return 'Orçamento'
  if (k === 'pedido') return 'Pedido'
  return kind?.trim() || '—'
}

export type AdmDespesasBudgetsTabProps = {
  budgets: AdmOrderHeader[]
  /** Itens de todos os cabeçalhos (aba «Itens»). */
  allItems: AdmOrderItem[]
  categorias: AdmCategoria[]
  centrosCusto: AdmCentroCusto[]
  categoryMap: Map<string, string>
  costCenterMap: Map<string, string>
  canEdit: boolean
  canDelete: boolean
  userId: string | undefined
  onRefresh: () => void
  pendingDetailDocumentId?: string | null
  onPendingDetailHandled?: () => void
  onError: (message: string | null) => void
  getErrorMessage: (error: unknown) => string
}

type OrderItemEditModalState = {
  itemId: string
  orderId: string
  orderLabel: string
  description: string
  ncm: string
  quantity: string
  unit: string
  unitValue: string
  totalValue: string
  categoryId: string
  costCenterId: string
}

const AdmDespesasBudgetsTab: React.FC<AdmDespesasBudgetsTabProps> = ({
  budgets,
  allItems,
  categorias,
  centrosCusto,
  categoryMap,
  costCenterMap,
  canEdit,
  canDelete,
  userId,
  onRefresh,
  pendingDetailDocumentId = null,
  onPendingDetailHandled,
  onError,
  getErrorMessage,
}) => {
  const [numberFilter, setNumberFilter] = React.useState('')
  const [issuerFilter, setIssuerFilter] = React.useState('')
  const [recipientFilter, setRecipientFilter] = React.useState('')
  const [dateFrom, setDateFrom] = React.useState('')
  const [dateTo, setDateTo] = React.useState('')
  const [paymentDateFrom, setPaymentDateFrom] = React.useState('')
  const [paymentDateTo, setPaymentDateTo] = React.useState('')
  const [fileNameFilter, setFileNameFilter] = React.useState('')

  const [page, setPage] = React.useState(1)
  const [sortKey, setSortKey] = React.useState('issue_date')
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc')

  const [detail, setDetail] = React.useState<{ header: AdmOrderHeader; items: AdmOrderItem[] } | null>(null)
  const [loadingDetail, setLoadingDetail] = React.useState(false)
  const [budgetEditModal, setBudgetEditModal] = React.useState<BudgetEditModalState | null>(null)
  const [submittingEdit, setSubmittingEdit] = React.useState(false)
  const [docDownloadBusy, setDocDownloadBusy] = React.useState(false)
  const [editDownloadError, setEditDownloadError] = React.useState<string | null>(null)

  const [deleteModal, setDeleteModal] = React.useState<{ id: string; label: string } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const [orderItemEditModal, setOrderItemEditModal] = React.useState<OrderItemEditModalState | null>(null)
  const [submittingOrderItemEdit, setSubmittingOrderItemEdit] = React.useState(false)
  const [deletingOrderItemId, setDeletingOrderItemId] = React.useState<string | null>(null)

  const [printDialogOpen, setPrintDialogOpen] = React.useState(false)
  const [mainSubTab, setMainSubTab] = React.useState<BudgetsMainSubTab>('headers')

  const [itemsSearch, setItemsSearch] = React.useState('')
  const [itemsCategory, setItemsCategory] = React.useState('')
  const [itemsCostCenter, setItemsCostCenter] = React.useState('')
  const [itemsPage, setItemsPage] = React.useState(1)
  const [itemsSortKey, setItemsSortKey] = React.useState('description')
  const [itemsSortOrder, setItemsSortOrder] = React.useState<'asc' | 'desc'>('asc')

  const budgetHeaderMap = React.useMemo(() => new Map(budgets.map((b) => [b.id, b])), [budgets])
  const activeBudgetIds = React.useMemo(() => new Set(budgets.map((b) => b.id)), [budgets])
  const itemsCountByBudget = React.useMemo(() => {
    const m = new Map<string, number>()
    for (const it of allItems) {
      if (!activeBudgetIds.has(it.order_id)) continue
      m.set(it.order_id, (m.get(it.order_id) ?? 0) + 1)
    }
    return m
  }, [allItems, activeBudgetIds])

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
    setPage(1)
  }

  const filtered = React.useMemo(() => {
    const num = numberFilter.trim().toLowerCase()
    const iss = issuerFilter.trim().toLowerCase()
    const rec = recipientFilter.trim().toLowerCase()
    const qItems = itemsSearch.trim().toLowerCase()
    const itemLevel = !!(qItems || itemsCategory || itemsCostCenter)
    return budgets.filter((b) => {
      if (num && !(b.number ?? '').toLowerCase().includes(num)) return false
      if (iss && !(b.issuer_name ?? '').toLowerCase().includes(iss) && !(b.issuer_cnpj ?? '').includes(iss)) return false
      if (rec && !(b.recipient_name ?? '').toLowerCase().includes(rec) && !(b.recipient_document ?? '').includes(rec))
        return false
      const id = b.issue_date ? b.issue_date.slice(0, 10) : ''
      if (dateFrom && id && id < dateFrom) return false
      if (dateTo && id && id > dateTo) return false
      if (!fileNameQueryMatches(b.file_name, fileNameFilter)) return false
      if (!matchesPaymentDatePeriod(b.payment_date, paymentDateFrom, paymentDateTo)) return false
      if (itemLevel) {
        const orderItems = allItems.filter((it) => it.order_id === b.id && activeBudgetIds.has(it.order_id))
        if (orderItems.length === 0) return false
        const any = orderItems.some((it) => {
          if (qItems && !(it.description ?? '').toLowerCase().includes(qItems)) return false
          if (itemsCategory && it.category_id !== itemsCategory) return false
          if (itemsCostCenter && it.cost_center_id !== itemsCostCenter) return false
          return true
        })
        if (!any) return false
      }
      return true
    })
  }, [
    budgets,
    numberFilter,
    issuerFilter,
    recipientFilter,
    dateFrom,
    dateTo,
    paymentDateFrom,
    paymentDateTo,
    fileNameFilter,
    allItems,
    activeBudgetIds,
    itemsSearch,
    itemsCategory,
    itemsCostCenter,
  ])

  const sorted = React.useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (sortKey === 'document_kind') {
        av = (a.document_kind ?? '').toLowerCase()
        bv = (b.document_kind ?? '').toLowerCase()
      } else if (sortKey === 'number') {
        av = (a.number ?? '').toLowerCase()
        bv = (b.number ?? '').toLowerCase()
      } else if (sortKey === 'issue_date') {
        av = a.issue_date ?? ''
        bv = b.issue_date ?? ''
      } else if (sortKey === 'total_value') {
        av = a.total_value ?? 0
        bv = b.total_value ?? 0
      }
      if (av < bv) return sortOrder === 'asc' ? -1 : 1
      if (av > bv) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filtered, sortKey, sortOrder])

  const pagesCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const start = (page - 1) * PAGE_SIZE
  const paginated = sorted.slice(start, start + PAGE_SIZE)

  const toggleItemsSort = (key: string) => {
    if (itemsSortKey === key) {
      setItemsSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setItemsSortKey(key)
      setItemsSortOrder('asc')
    }
    setItemsPage(1)
  }

  const filteredItems = React.useMemo(() => {
    const q = itemsSearch.trim().toLowerCase()
    const num = numberFilter.trim().toLowerCase()
    const iss = issuerFilter.trim().toLowerCase()
    const rec = recipientFilter.trim().toLowerCase()
    return allItems.filter((it) => {
      if (!activeBudgetIds.has(it.order_id)) return false
      const h = budgetHeaderMap.get(it.order_id)
      if (!h) return false
      if (num && !(h.number ?? '').toLowerCase().includes(num)) return false
      if (iss && !(h.issuer_name ?? '').toLowerCase().includes(iss) && !(h.issuer_cnpj ?? '').includes(iss)) return false
      if (rec && !(h.recipient_name ?? '').toLowerCase().includes(rec) && !(h.recipient_document ?? '').includes(rec))
        return false
      const id = h.issue_date ? h.issue_date.slice(0, 10) : ''
      if (dateFrom && id && id < dateFrom) return false
      if (dateTo && id && id > dateTo) return false
      if (!fileNameQueryMatches(h.file_name, fileNameFilter)) return false
      if (!matchesPaymentDatePeriod(h.payment_date, paymentDateFrom, paymentDateTo)) return false
      if (q && !(it.description ?? '').toLowerCase().includes(q)) return false
      if (itemsCategory && it.category_id !== itemsCategory) return false
      if (itemsCostCenter && it.cost_center_id !== itemsCostCenter) return false
      return true
    })
  }, [
    allItems,
    activeBudgetIds,
    budgetHeaderMap,
    itemsSearch,
    numberFilter,
    issuerFilter,
    recipientFilter,
    dateFrom,
    dateTo,
    paymentDateFrom,
    paymentDateTo,
    fileNameFilter,
    itemsCategory,
    itemsCostCenter,
  ])

  const sortedItems = React.useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const ha = budgetHeaderMap.get(a.order_id)
      const hb = budgetHeaderMap.get(b.order_id)
      let av: string | number = ''
      let bv: string | number = ''
      if (itemsSortKey === 'budget_number') {
        av = (ha?.number ?? '').toLowerCase()
        bv = (hb?.number ?? '').toLowerCase()
      } else if (itemsSortKey === 'description') {
        av = (a.description ?? '').toLowerCase()
        bv = (b.description ?? '').toLowerCase()
      } else if (itemsSortKey === 'total_value') {
        av = a.total_value ?? 0
        bv = b.total_value ?? 0
      } else if (itemsSortKey === 'quantity') {
        av = a.quantity ?? 0
        bv = b.quantity ?? 0
      }
      if (av < bv) return itemsSortOrder === 'asc' ? -1 : 1
      if (av > bv) return itemsSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredItems, budgetHeaderMap, itemsSortKey, itemsSortOrder])

  const itemsPagesCount = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE))
  const itemsStart = (itemsPage - 1) * PAGE_SIZE
  const paginatedItems = sortedItems.slice(itemsStart, itemsStart + PAGE_SIZE)

  const budgetsHeadersTotalValue = React.useMemo(
    () => sorted.reduce((sum, b) => sum + (b.total_value ?? 0), 0),
    [sorted],
  )
  const budgetItemsUnitValueTotal = React.useMemo(
    () => sortedItems.reduce((sum, it) => sum + (it.unit_value ?? 0), 0),
    [sortedItems],
  )
  const budgetItemsQuantityTotal = React.useMemo(
    () => sortedItems.reduce((sum, it) => sum + (it.quantity ?? 0), 0),
    [sortedItems],
  )
  const budgetItemsTotalValue = React.useMemo(
    () => sortedItems.reduce((sum, it) => sum + (it.total_value ?? 0), 0),
    [sortedItems],
  )
  const budgetItemsUnitValueAverage = React.useMemo(() => {
    const n = sortedItems.length
    if (n === 0) return 0
    return sortedItems.reduce((sum, it) => sum + (it.unit_value ?? 0), 0) / n
  }, [sortedItems])

  const runBudgetDownload = React.useCallback(
    async (fileNameStorage: string | null | undefined, fileName: string | null | undefined) =>
      runAdmStorageSignedDownload(supabase, {
        fileNameStorage,
        fileName,
        storageFolderPrefix: ADM_BUDGET_STORAGE_FOLDER,
        signedUrlTtlSec: 600,
      }),
    []
  )

  const openSignedDownload = React.useCallback(
    async (fileNameStorage: string | null | undefined, fileName: string | null | undefined) => {
      if (!hasAdmNotaDownloadKey(fileNameStorage, fileName)) return
      setDocDownloadBusy(true)
      onError(null)
      try {
        const r = await runBudgetDownload(fileNameStorage, fileName)
        if (!r.ok) onError(r.message)
      } finally {
        setDocDownloadBusy(false)
      }
    },
    [runBudgetDownload, onError]
  )

  const openSignedDownloadFromEdit = React.useCallback(async () => {
    if (!budgetEditModal) return
    if (!hasAdmNotaDownloadKey(budgetEditModal.fileNameStorage, budgetEditModal.fileName)) {
      setEditDownloadError('Informe file_name ou file_name_storage para baixar o documento.')
      return
    }
    setDocDownloadBusy(true)
    setEditDownloadError(null)
    onError(null)
    try {
      const r = await runBudgetDownload(budgetEditModal.fileNameStorage, budgetEditModal.fileName)
      if (!r.ok) setEditDownloadError(r.message)
    } finally {
      setDocDownloadBusy(false)
    }
  }, [budgetEditModal, runBudgetDownload, onError])

  const openDetail = async (h: AdmOrderHeader) => {
    setLoadingDetail(true)
    onError(null)
    try {
      const items = await admDespesasService.listOrderItems(h.id)
      setDetail({ header: h, items })
    } catch (e) {
      onError(getErrorMessage(e))
    } finally {
      setLoadingDetail(false)
    }
  }

  const openEdit = (h: AdmOrderHeader) => {
    setEditDownloadError(null)
    setBudgetEditModal(budgetEditStateFromRecord(h))
  }

  React.useEffect(() => {
    if (!pendingDetailDocumentId) return
    const record = budgets.find((h) => h.id === pendingDetailDocumentId)
    if (record) void openDetail(record)
    onPendingDetailHandled?.()
  }, [pendingDetailDocumentId, budgets, onPendingDetailHandled])

  const handleSaveEdit = async () => {
    if (!budgetEditModal) return
    setSubmittingEdit(true)
    onError(null)
    try {
      const patch = budgetEditPatchFromModal(budgetEditModal)
      const prev = budgets.find((x) => x.id === budgetEditModal.id)
      await admDespesasService.updateOrderHeader(budgetEditModal.id, patch)
      await logUpdate(
        'other',
        budgetEditModal.id,
        `Atualizou orçamento/pedido ${patch.number ?? budgetEditModal.id}`,
        {
          document_kind: prev?.document_kind ?? null,
          number: prev?.number ?? null,
          issue_date: prev?.issue_date ?? null,
          issuer_name: prev?.issuer_name ?? null,
          issuer_cnpj: prev?.issuer_cnpj ?? null,
          recipient_name: prev?.recipient_name ?? null,
          recipient_document: prev?.recipient_document ?? null,
          total_products: prev?.total_products ?? null,
          total_freight: prev?.total_freight ?? null,
          total_discount: prev?.total_discount ?? null,
          total_value: prev?.total_value ?? null,
          payment_method: prev?.payment_method ?? null,
          payment_date: prev?.payment_date ?? null,
          manual_notes: prev?.manual_notes ?? null,
          file_name: prev?.file_name ?? null,
          file_name_storage: prev?.file_name_storage ?? null,
        },
        patch,
        { additional_info: 'Módulo ADM • Despesas (orçamento/pedido)' },
      )
      setDetail((d) =>
        d && d.header.id === budgetEditModal.id
          ? {
              ...d,
              header: {
                ...d.header,
                ...patch,
                document_kind: patch.document_kind ?? null,
                number: patch.number ?? null,
                issue_date: patch.issue_date ?? null,
                issuer_name: patch.issuer_name ?? null,
                issuer_cnpj: patch.issuer_cnpj ?? null,
                recipient_name: patch.recipient_name ?? null,
                recipient_document: patch.recipient_document ?? null,
                total_products: patch.total_products ?? null,
                total_freight: patch.total_freight ?? null,
                total_discount: patch.total_discount ?? null,
                total_value: patch.total_value ?? null,
                payment_method: patch.payment_method ?? null,
                payment_date: patch.payment_date ?? null,
                manual_notes: patch.manual_notes ?? null,
                file_name: patch.file_name ?? null,
                file_name_storage: patch.file_name_storage ?? null,
              },
            }
          : d,
      )
      setEditDownloadError(null)
      setBudgetEditModal(null)
      onRefresh()
    } catch (e) {
      onError(getErrorMessage(e))
    } finally {
      setSubmittingEdit(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteModal || !userId) return
    const reason = deleteReason.trim()
    if (!reason) return
    setSubmittingDelete(true)
    onError(null)
    try {
      await admDespesasService.softDeleteOrderHeader(deleteModal.id, reason, userId)
      await logDelete('other', deleteModal.id, `Excluiu orçamento/pedido: ${deleteModal.label}`, {
        additional_info: `Motivo: ${reason}`,
      })
      setDeleteModal(null)
      setDeleteReason('')
      setDetail((prev) => (prev?.header.id === deleteModal.id ? null : prev))
      onRefresh()
    } catch (e) {
      onError(getErrorMessage(e))
    } finally {
      setSubmittingDelete(false)
    }
  }

  const openOrderItemEdit = (it: AdmOrderItem) => {
    const h = budgetHeaderMap.get(it.order_id)
    const orderLabel = h ? `${formatDocumentKind(h.document_kind)} ${h.number || h.id.slice(0, 8)}` : it.order_id
    setOrderItemEditModal({
      itemId: it.id,
      orderId: it.order_id,
      orderLabel,
      description: it.description ?? '',
      ncm: it.ncm ?? '',
      quantity: it.quantity != null ? String(it.quantity) : '',
      unit: it.unit ?? '',
      unitValue: it.unit_value != null ? String(it.unit_value) : '',
      totalValue: it.total_value != null ? String(it.total_value) : '',
      categoryId: it.category_id ?? '',
      costCenterId: it.cost_center_id ?? '',
    })
  }

  const handleSaveOrderItemEdit = async () => {
    if (!orderItemEditModal || !userId) return
    const itemId = orderItemEditModal.itemId
    setSubmittingOrderItemEdit(true)
    onError(null)
    try {
      const patch = {
        description: orderItemEditModal.description.trim() || null,
        ncm: orderItemEditModal.ncm.trim() || null,
        quantity: orderItemEditModal.quantity.trim() ? Number(orderItemEditModal.quantity) : null,
        unit: orderItemEditModal.unit.trim() || null,
        unit_value: orderItemEditModal.unitValue.trim() ? Number(orderItemEditModal.unitValue) : null,
        total_value: orderItemEditModal.totalValue.trim() ? Number(orderItemEditModal.totalValue) : null,
        category_id: orderItemEditModal.categoryId.trim() || null,
        cost_center_id: orderItemEditModal.costCenterId.trim() || null,
      }
      const prev =
        allItems.find((i) => i.id === itemId) ?? detail?.items.find((i) => i.id === itemId)
      await admDespesasService.updateOrderItem(itemId, patch)
      await logUpdate(
        'other',
        itemId,
        `Atualizou item de orçamento/pedido (${orderItemEditModal.orderLabel})`,
        {
          description: prev?.description ?? null,
          ncm: prev?.ncm ?? null,
          quantity: prev?.quantity ?? null,
          unit: prev?.unit ?? null,
          unit_value: prev?.unit_value ?? null,
          total_value: prev?.total_value ?? null,
          category_id: prev?.category_id ?? null,
          cost_center_id: prev?.cost_center_id ?? null,
        },
        patch,
        { additional_info: `Cabeçalho ID: ${orderItemEditModal.orderId}` },
      )
      setOrderItemEditModal(null)
      setDetail((prevD) =>
        prevD
          ? {
              ...prevD,
              items: prevD.items.map((i) => (i.id === itemId ? { ...i, ...patch } : i)),
            }
          : prevD,
      )
      onRefresh()
    } catch (e) {
      onError(getErrorMessage(e))
    } finally {
      setSubmittingOrderItemEdit(false)
    }
  }

  const handleSoftDeleteOrderItem = async (it: AdmOrderItem) => {
    if (!userId) return
    const h = budgetHeaderMap.get(it.order_id)
    const docLabel = h ? `${formatDocumentKind(h.document_kind)} ${h.number || h.id.slice(0, 8)}` : it.order_id
    const confirmed = window.confirm(`Confirma a exclusão do item "${it.description ?? it.id}" (${docLabel})?`)
    if (!confirmed) return
    setDeletingOrderItemId(it.id)
    onError(null)
    try {
      await admDespesasService.softDeleteOrderItem(it.id, userId)
      await logDelete('other', it.id, `Excluiu item de orçamento/pedido (${docLabel})`, {
        additional_info: `order_id: ${it.order_id}`,
      })
      setDetail((d) => (d ? { ...d, items: d.items.filter((i) => i.id !== it.id) } : d))
      onRefresh()
    } catch (e) {
      onError(getErrorMessage(e))
    } finally {
      setDeletingOrderItemId(null)
    }
  }

  const buildPrintPayload = (scope: 'page' | 'filtered'): PrintConfig => {
    const generatedAt = new Date().toLocaleString('pt-BR')
    const scopeLabel = scope === 'page' ? 'Página atual' : 'Todos os filtrados'

    if (mainSubTab === 'items') {
      const source = scope === 'page' ? paginatedItems : sortedItems
      const columns: PrintColumn[] = [
        { key: 'budget', label: 'Orç./Ped.' },
        { key: 'description', label: 'Descrição' },
        { key: 'quantity', label: 'Qtd', align: 'right' },
        { key: 'total_value', label: 'Total', align: 'right' },
        { key: 'category', label: 'Categoria' },
        { key: 'cost_center', label: 'Centro de Custo' },
      ]
      const rows: PrintRow[] = source.map((it) => {
        const h = budgetHeaderMap.get(it.order_id)
        return {
          budget: `${formatDocumentKind(h?.document_kind ?? null)} ${h?.number ?? '—'}`,
          description: it.description || '—',
          quantity: it.quantity ?? '—',
          total_value: formatCurrency(it.total_value),
          category: it.category_id ? categoryMap.get(it.category_id) ?? it.category_id : '—',
          cost_center: it.cost_center_id ? costCenterMap.get(it.cost_center_id) ?? it.cost_center_id : '—',
        }
      })
      return {
        reportTitle: 'ADM • Despesas • Orçamentos/Pedidos • Itens',
        generatedAt,
        scopeLabel,
        totalRows: rows.length,
        filters: [
          { label: 'Número', value: numberFilter },
          { label: 'Emitente', value: issuerFilter },
          { label: 'Cliente', value: recipientFilter },
          { label: 'Emissão de', value: dateFrom },
          { label: 'Emissão até', value: dateTo },
          { label: 'Pagamento de', value: paymentDateFrom },
          { label: 'Pagamento até', value: paymentDateTo },
          { label: FILTRO_NOME_ARQUIVO, value: fileNameFilter },
          { label: 'Descrição', value: itemsSearch },
          { label: 'Categoria', value: itemsCategory ? categoryMap.get(itemsCategory) ?? itemsCategory : '' },
          { label: 'Centro de Custo', value: itemsCostCenter ? costCenterMap.get(itemsCostCenter) ?? itemsCostCenter : '' },
        ].filter((f) => String(f.value).trim()),
        columns,
        rows,
      }
    }

    const source = scope === 'page' ? paginated : sorted
    const columns: PrintColumn[] = [
      { key: 'document_kind', label: 'Tipo' },
      { key: 'number', label: 'Número' },
      { key: 'issuer_name', label: 'Emitente' },
      { key: 'recipient_name', label: 'Cliente' },
      { key: 'issue_date', label: 'Emissão' },
      { key: 'items_count', label: 'Nº itens', align: 'right' },
      { key: 'total_value', label: 'Total', align: 'right' },
    ]
    const rows: PrintRow[] = source.map((b) => ({
      document_kind: formatDocumentKind(b.document_kind),
      number: b.number || '—',
      issuer_name: b.issuer_name || '—',
      recipient_name: b.recipient_name || '—',
      issue_date: formatDate(b.issue_date),
      items_count: itemsCountByBudget.get(b.id) ?? 0,
      total_value: formatCurrency(b.total_value),
    }))
    return {
      reportTitle: 'ADM • Despesas • Orçamentos/Pedidos',
      generatedAt,
      scopeLabel,
      totalRows: rows.length,
      filters: [
        { label: 'Número', value: numberFilter },
        { label: 'Emitente', value: issuerFilter },
        { label: 'Cliente', value: recipientFilter },
        { label: 'Emissão de', value: dateFrom },
        { label: 'Emissão até', value: dateTo },
        { label: 'Pagamento de', value: paymentDateFrom },
        { label: 'Pagamento até', value: paymentDateTo },
        { label: FILTRO_NOME_ARQUIVO, value: fileNameFilter },
        { label: 'Descrição', value: itemsSearch },
        { label: 'Categoria', value: itemsCategory ? categoryMap.get(itemsCategory) ?? itemsCategory : '' },
        { label: 'Centro de Custo', value: itemsCostCenter ? costCenterMap.get(itemsCostCenter) ?? itemsCostCenter : '' },
      ].filter((f) => String(f.value).trim()),
      columns,
      rows,
    }
  }

  const mainSubTabClass = (active: boolean) =>
    active
      ? 'border-primary-500 text-primary-600 dark:text-primary-400 whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm'

  const renderTotalsSummary = (
    totals: Array<{ label: string; value: number; kind?: 'currency' | 'quantity' }>,
  ) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <div className="flex flex-nowrap items-stretch gap-2 sm:gap-3 overflow-x-auto pb-0.5">
        {totals.map((item) => (
          <div
            key={item.label}
            className="flex min-w-[6.75rem] flex-1 flex-col rounded-lg border border-gray-200 dark:border-gray-700 px-2 sm:min-w-0 sm:px-3 py-2"
          >
            <Text size="xs" color="muted" className="uppercase tracking-wide">
              {item.label}
            </Text>
            <Text size="sm" className="font-semibold text-gray-900 dark:text-white mt-0.5 tabular-nums">
              {item.kind === 'quantity' ? formatQuantity(item.value) : formatCurrency(item.value)}
            </Text>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <>
      <Text size="sm" color="muted" className="mb-2 block">
        Orçamentos e pedidos (cabeçalho e itens), espelhando a estrutura da NF-e.
      </Text>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="mb-3">
          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
            Filtros
          </Heading>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 [&>*]:min-w-0">
          <Input
            placeholder="Número"
            title="Número do orçamento ou pedido (cabeçalho e itens)"
            value={numberFilter}
            onChange={(e) => {
              setNumberFilter(e.target.value)
              setPage(1)
              setItemsPage(1)
            }}
          />
          <Input
            placeholder="Emitente (nome ou CNPJ)"
            value={issuerFilter}
            onChange={(e) => {
              setIssuerFilter(e.target.value)
              setPage(1)
              setItemsPage(1)
            }}
          />
          <Input
            placeholder="Cliente (nome ou documento)"
            value={recipientFilter}
            onChange={(e) => {
              setRecipientFilter(e.target.value)
              setPage(1)
              setItemsPage(1)
            }}
          />
          <div
            className="flex min-w-0 items-center gap-2"
            title="Data inicial de emissão (inclusiva), conforme coluna issue_date"
          >
            <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
              Emissão de
            </span>
            <Input
              type="date"
              className="min-w-0 flex-1"
              aria-label="Data de emissão inicial"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value)
                setPage(1)
                setItemsPage(1)
              }}
            />
          </div>
          <div
            className="flex min-w-0 items-center gap-2"
            title="Data final de emissão (inclusiva), conforme coluna issue_date"
          >
            <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
              Emissão até
            </span>
            <Input
              type="date"
              className="min-w-0 flex-1"
              aria-label="Data de emissão final"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value)
                setPage(1)
                setItemsPage(1)
              }}
            />
          </div>
          <PaymentDatePeriodFilterFields
            from={paymentDateFrom}
            to={paymentDateTo}
            onFromChange={setPaymentDateFrom}
            onToChange={setPaymentDateTo}
            onChange={() => {
              setPage(1)
              setItemsPage(1)
            }}
          />
          <Input
            type="text"
            placeholder={FILTRO_NOME_ARQUIVO}
            title={FILTRO_NOME_ARQUIVO_TITLE}
            value={fileNameFilter}
            onChange={(e) => {
              setFileNameFilter(e.target.value)
              setPage(1)
              setItemsPage(1)
            }}
          />
          <Input
            placeholder="Descrição (itens)"
            title="Filtra pela descrição dos itens (ambas as subabas)"
            value={itemsSearch}
            onChange={(e) => {
              setItemsSearch(e.target.value)
              setPage(1)
              setItemsPage(1)
            }}
          />
          <select
            value={itemsCategory}
            onChange={(e) => {
              setItemsCategory(e.target.value)
              setPage(1)
              setItemsPage(1)
            }}
            className="min-w-0 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
          >
            <option value="">Categoria: Todas</option>
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={itemsCostCenter}
            onChange={(e) => {
              setItemsCostCenter(e.target.value)
              setPage(1)
              setItemsPage(1)
            }}
            className="min-w-0 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
          >
            <option value="">Centro de custo: Todos</option>
            {centrosCusto.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <div className="col-span-full flex flex-wrap justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setNumberFilter('')
                setIssuerFilter('')
                setRecipientFilter('')
                setDateFrom('')
                setDateTo('')
                setPaymentDateFrom('')
                setPaymentDateTo('')
                setFileNameFilter('')
                setItemsSearch('')
                setItemsCategory('')
                setItemsCostCenter('')
                setPage(1)
                setItemsPage(1)
              }}
            >
              Limpar filtros
            </Button>
            <Button variant="outline" size="sm" type="button" onClick={() => setPrintDialogOpen(true)}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              Imprimir
            </Button>
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex flex-wrap gap-x-6">
          <button
            type="button"
            onClick={() => {
              setMainSubTab('headers')
              setPage(1)
            }}
            className={mainSubTabClass(mainSubTab === 'headers')}
          >
            Cabeçalhos
          </button>
          <button
            type="button"
            onClick={() => {
              setMainSubTab('items')
              setItemsPage(1)
            }}
            className={mainSubTabClass(mainSubTab === 'items')}
          >
            Itens
            {sortedItems.length > 0 && (
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {sortedItems.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {mainSubTab === 'headers' && (
        <>
          {renderTotalsSummary([{ label: 'Soma Valor Total', value: budgetsHeadersTotalValue }])}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        {paginated.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
            <Text size="sm" color="muted">
              {budgets.length === 0
                ? 'Nenhum orçamento ou pedido cadastrado.'
                : 'Nenhum registro encontrado com os filtros aplicados.'}
            </Text>
          </div>
        ) : (
          <>
            <table className="w-full min-w-[880px] divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <SortableTableHeader sortKey="document_kind" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Tipo
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="number" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Número
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="issue_date" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Emissão
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Emitente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Nº itens</th>
                  <SortableTableHeader sortKey="total_value" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3 text-right">
                    Total
                  </SortableTableHeader>
                  {(canDelete || canEdit) && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginated.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => void openDetail(b)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {formatDocumentKind(b.document_kind)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">{b.number || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatDate(b.issue_date)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[200px] truncate" title={b.issuer_name ?? ''}>
                      {b.issuer_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[200px] truncate" title={b.recipient_name ?? ''}>
                      {b.recipient_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">
                      {itemsCountByBudget.get(b.id) ?? 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(b.total_value)}</td>
                    {(canDelete || canEdit) && (
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-2">
                          {canEdit && (
                            <button
                              type="button"
                              title="Editar"
                              onClick={() => openEdit(b)}
                              className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              title="Excluir"
                              onClick={() => {
                                setDeleteModal({ id: b.id, label: b.number || b.id })
                                setDeleteReason('')
                              }}
                              className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <Text size="sm" color="muted">
                {sorted.length === 0
                  ? '0 registros'
                  : `${start + 1}–${Math.min(start + PAGE_SIZE, sorted.length)} de ${sorted.length}`}
              </Text>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Anterior
                </Button>
                <Button variant="outline" size="sm" disabled={page >= pagesCount} onClick={() => setPage(page + 1)}>
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
          </div>
        </>
      )}

      {mainSubTab === 'items' && (
        <>
          {renderTotalsSummary([
            { label: 'Soma Qtd', value: budgetItemsQuantityTotal, kind: 'quantity' },
            { label: 'Soma Vl. Unit.', value: budgetItemsUnitValueTotal },
            { label: 'Média Vl. Unit.', value: budgetItemsUnitValueAverage },
            { label: 'Soma Total', value: budgetItemsTotalValue },
          ])}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
            {paginatedItems.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                <Text size="sm" color="muted">
                  {allItems.length === 0 ? 'Nenhum item cadastrado.' : 'Nenhum item encontrado com os filtros.'}
                </Text>
              </div>
            ) : (
              <>
                <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <SortableTableHeader
                        sortKey="budget_number"
                        currentSort={itemsSortKey}
                        sortOrder={itemsSortOrder}
                        onSort={toggleItemsSort}
                        className="px-4 py-3"
                      >
                        Orç./Ped.
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="description"
                        currentSort={itemsSortKey}
                        sortOrder={itemsSortOrder}
                        onSort={toggleItemsSort}
                        className="px-4 py-3"
                      >
                        Descrição
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="quantity"
                        currentSort={itemsSortKey}
                        sortOrder={itemsSortOrder}
                        onSort={toggleItemsSort}
                        className="px-4 py-3 text-right"
                      >
                        Qtd
                      </SortableTableHeader>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Un.</th>
                      <SortableTableHeader
                        sortKey="total_value"
                        currentSort={itemsSortKey}
                        sortOrder={itemsSortOrder}
                        onSort={toggleItemsSort}
                        className="px-4 py-3 text-right"
                      >
                        Total
                      </SortableTableHeader>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Centro de Custo</th>
                      {(canDelete || canEdit) && (
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedItems.map((it) => {
                      const h = budgetHeaderMap.get(it.order_id)
                      const label = h ? `${formatDocumentKind(h.document_kind)} ${h.number || h.id.slice(0, 8)}` : it.order_id
                      return (
                        <tr
                          key={it.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                          onClick={() => {
                            if (h) void openDetail(h)
                          }}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap max-w-[180px] truncate" title={label}>
                            {label}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[240px] truncate" title={it.description ?? ''}>
                            {it.description || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{it.quantity ?? '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{it.unit || '—'}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(it.total_value)}</td>
                          <td className="px-4 py-3 min-w-[160px]">
                            {it.category_id ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                {categoryMap.get(it.category_id) ?? it.category_id}
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 min-w-[160px]">
                            {it.cost_center_id ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                {costCenterMap.get(it.cost_center_id) ?? it.cost_center_id}
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                            )}
                          </td>
                          {(canDelete || canEdit) && (
                            <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-2">
                                {canEdit && (
                                  <button
                                    type="button"
                                    title="Editar item"
                                    onClick={() => openOrderItemEdit(it)}
                                    className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    type="button"
                                    title="Excluir item"
                                    onClick={() => void handleSoftDeleteOrderItem(it)}
                                    disabled={deletingOrderItemId === it.id}
                                    className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                  <Text size="sm" color="muted">
                    {sortedItems.length === 0
                      ? '0 registros'
                      : `${itemsStart + 1}–${Math.min(itemsStart + PAGE_SIZE, sortedItems.length)} de ${sortedItems.length}`}
                  </Text>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={itemsPage <= 1} onClick={() => setItemsPage(itemsPage - 1)}>
                      Anterior
                    </Button>
                    <Button variant="outline" size="sm" disabled={itemsPage >= itemsPagesCount} onClick={() => setItemsPage(itemsPage + 1)}>
                      Próxima
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {formatDocumentKind(detail.header.document_kind)} {detail.header.number || detail.header.id.slice(0, 8)}
              </Heading>
              <div className="flex items-center gap-2">
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(detail.header)}>
                    Editar cabeçalho
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setDetail(null)}>
                  Fechar
                </Button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {loadingDetail && (
                <div className="flex justify-center py-6">
                  <LoadingSpinner />
                </div>
              )}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Cabeçalho
                </Text>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Tipo</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocumentKind(detail.header.document_kind)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Número</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detail.header.number || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Emissão</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(detail.header.issue_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Meio de pagamento</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detail.header.payment_method || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Data pagamento</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(detail.header.payment_date)}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Observações manuais</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap break-words">{detail.header.manual_notes || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Emitente</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detail.header.issuer_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">CNPJ emitente</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detail.header.issuer_cnpj)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Cliente</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detail.header.recipient_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Documento cliente</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detail.header.recipient_document)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Total produtos</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatCurrency(detail.header.total_products)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Frete</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatCurrency(detail.header.total_freight)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Desconto</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatCurrency(detail.header.total_discount)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Total</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 font-semibold">{formatCurrency(detail.header.total_value)}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Arquivo</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">
                      {(detail.header.file_name ||
                        hasAdmNotaDownloadKey(detail.header.file_name_storage, detail.header.file_name)) ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-1 text-sm">
                            <DocumentIcon className="h-4 w-4 text-gray-400 shrink-0" />
                            {detail.header.file_name ?? '—'}
                          </span>
                          {hasAdmNotaDownloadKey(detail.header.file_name_storage, detail.header.file_name) ? (
                            <button
                              type="button"
                              onClick={() =>
                                void openSignedDownload(detail.header.file_name_storage, detail.header.file_name)
                              }
                              disabled={docDownloadBusy}
                              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
                            >
                              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                              Baixar documento
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Itens ({detail.items.length})
                </Text>
                {detail.items.length === 0 ? (
                  <Text size="sm" color="muted">
                    Nenhum item.
                  </Text>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Descrição</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">NCM</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Qtd</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Un.</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vl. unit.</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Categoria</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Centro de Custo</th>
                          {(canDelete || canEdit) && (
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ações</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {detail.items.map((it) => (
                          <tr key={it.id}>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100 max-w-[200px]">{it.description || '—'}</td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{it.ncm || '—'}</td>
                            <td className="px-3 py-2 text-right">{it.quantity ?? '—'}</td>
                            <td className="px-3 py-2">{it.unit || '—'}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(it.unit_value)}</td>
                            <td className="px-3 py-2 text-right font-medium">{formatCurrency(it.total_value)}</td>
                            <td className="px-3 py-2 min-w-[160px]">
                              {it.category_id ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                  {categoryMap.get(it.category_id) ?? it.category_id}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 min-w-[160px]">
                              {it.cost_center_id ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                  {costCenterMap.get(it.cost_center_id) ?? it.cost_center_id}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                              )}
                            </td>
                            {(canDelete || canEdit) && (
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  {canEdit && (
                                    <button
                                      type="button"
                                      title="Editar item"
                                      onClick={() => openOrderItemEdit(it)}
                                      className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      type="button"
                                      title="Excluir item"
                                      onClick={() => void handleSoftDeleteOrderItem(it)}
                                      disabled={deletingOrderItemId === it.id}
                                      className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {budgetEditModal && (
        <AdmDespesasBudgetEditModal
          modal={budgetEditModal}
          submitting={submittingEdit}
          downloadBusy={docDownloadBusy}
          downloadError={editDownloadError}
          onChange={(updater) => setBudgetEditModal((prev) => (prev ? updater(prev) : prev))}
          onClose={() => !submittingEdit && setBudgetEditModal(null)}
          onSave={() => void handleSaveEdit()}
          onDownload={() => void openSignedDownloadFromEdit()}
        />
      )}

      {orderItemEditModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="order-item-edit-title"
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto"
          >
            <Heading as="h3" id="order-item-edit-title" size="2xl" className="text-gray-900 dark:text-white mb-4">
              Editar item
            </Heading>
            <div className="space-y-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Text size="sm" color="muted">
                  Documento: {orderItemEditModal.orderLabel}
                </Text>
                <Text size="sm" color="muted" className="truncate block">
                  Item: {orderItemEditModal.description || orderItemEditModal.itemId}
                </Text>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Descrição</label>
                <Input
                  type="text"
                  value={orderItemEditModal.description}
                  onChange={(e) =>
                    setOrderItemEditModal((prev) => (prev ? { ...prev, description: e.target.value } : prev))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">NCM</label>
                <Input
                  type="text"
                  value={orderItemEditModal.ncm}
                  onChange={(e) => setOrderItemEditModal((prev) => (prev ? { ...prev, ncm: e.target.value } : prev))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Unidade</label>
                <Input
                  type="text"
                  value={orderItemEditModal.unit}
                  onChange={(e) => setOrderItemEditModal((prev) => (prev ? { ...prev, unit: e.target.value } : prev))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Quantidade</label>
                <Input
                  type="number"
                  step="any"
                  value={orderItemEditModal.quantity}
                  onChange={(e) =>
                    setOrderItemEditModal((prev) => (prev ? { ...prev, quantity: e.target.value } : prev))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Valor unitário</label>
                <Input
                  type="number"
                  step="any"
                  value={orderItemEditModal.unitValue}
                  onChange={(e) =>
                    setOrderItemEditModal((prev) => (prev ? { ...prev, unitValue: e.target.value } : prev))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Valor total</label>
                <Input
                  type="number"
                  step="any"
                  value={orderItemEditModal.totalValue}
                  onChange={(e) =>
                    setOrderItemEditModal((prev) => (prev ? { ...prev, totalValue: e.target.value } : prev))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Categoria</label>
                <select
                  value={orderItemEditModal.categoryId}
                  onChange={(e) =>
                    setOrderItemEditModal((prev) => (prev ? { ...prev, categoryId: e.target.value } : prev))
                  }
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
                >
                  <option value="">— Nenhuma —</option>
                  {categorias.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Centro de custo</label>
                <select
                  value={orderItemEditModal.costCenterId}
                  onChange={(e) =>
                    setOrderItemEditModal((prev) => (prev ? { ...prev, costCenterId: e.target.value } : prev))
                  }
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
                >
                  <option value="">— Nenhum —</option>
                  {centrosCusto.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => !submittingOrderItemEdit && setOrderItemEditModal(null)}
                disabled={submittingOrderItemEdit}
              >
                Cancelar
              </Button>
              <Button variant="solid" onClick={() => void handleSaveOrderItemEdit()} disabled={submittingOrderItemEdit}>
                {submittingOrderItemEdit ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Confirmar exclusão
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Excluindo: <strong className="text-gray-900 dark:text-white">{deleteModal.label}</strong>
            </Text>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motivo <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setDeleteModal(null)} disabled={submittingDelete}>
                Cancelar
              </Button>
              <Button
                variant="solid"
                className="!bg-red-600 hover:!bg-red-700"
                onClick={() => void confirmDelete()}
                disabled={submittingDelete || !deleteReason.trim()}
              >
                {submittingDelete ? 'Excluindo...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {printDialogOpen && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Imprimir
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              {mainSubTab === 'items'
                ? 'Escopo da tabela de itens (orçamentos e pedidos).'
                : 'Escopo da listagem de cabeçalhos de orçamentos e pedidos.'}
            </Text>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => {
                  printService.print(buildPrintPayload('page'))
                  setPrintDialogOpen(false)
                }}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 text-left"
              >
                Página atual
              </button>
              <button
                type="button"
                onClick={() => {
                  printService.print(buildPrintPayload('filtered'))
                  setPrintDialogOpen(false)
                }}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 text-left"
              >
                Todos os filtrados
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AdmDespesasBudgetsTab
